"use client";
/*
 * Documentation:
 * Button — https://app.subframe.com/4f62853ee923/library?component=Button_3b777358-b86b-40af-9327-891efc6826fe
 * DC_Popover_Card_CiteAs — https://app.subframe.com/4f62853ee923/library?component=DC_Popover_Card_CiteAs_fc59b4a1-0da2-4f46-82c2-c74633d042c4
 */

import React from "react";
import { FeatherCheck } from "@subframe/core";
import { FeatherClipboard } from "@subframe/core";
import { FeatherLayers2 } from "@subframe/core";
import * as SubframeUtils from "../utils";
import { Button } from "./Button";

interface CopyInteractionProps extends React.HTMLAttributes<HTMLDivElement> {
  pressed?: boolean;
  text?: React.ReactNode;
  copiedText?: React.ReactNode;
  className?: string;
}

const CopyInteraction = React.forwardRef<HTMLDivElement, CopyInteractionProps>(
  function CopyInteraction(
    {
      pressed = false,
      text,
      copiedText,
      className,
      ...otherProps
    }: CopyInteractionProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/4450ad66 flex w-full flex-col items-start gap-2",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <div className="flex w-full flex-col items-start gap-2">
          <Button
            className={SubframeUtils.twClassNames("h-8 w-full flex-none", {
              hidden: pressed,
            })}
            disabled={false}
            variant="brand-secondary"
            size="medium"
            icon={<FeatherClipboard />}
            iconRight={null}
            loading={false}
          >
            {text}
          </Button>
          <Button
            className={SubframeUtils.twClassNames(
              "hidden h-8 w-full flex-none",
              { flex: pressed }
            )}
            disabled={false}
            variant="brand-secondary"
            size="medium"
            iconRight={<FeatherCheck />}
            loading={false}
          >
            {copiedText}
          </Button>
        </div>
      </div>
    );
  }
);

interface CiteAsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  version?: React.ReactNode;
  children?: React.ReactNode;
  linkSlot?: React.ReactNode;
  versionLinkSlot?: React.ReactNode;
  className?: string;
}

const CiteAsContent = React.forwardRef<HTMLDivElement, CiteAsContentProps>(
  function CiteAsContent(
    {
      version,
      children,
      linkSlot,
      versionLinkSlot,
      className,
      ...otherProps
    }: CiteAsContentProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex flex-col items-start gap-2",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {versionLinkSlot ? (
          <div className="flex w-full items-center gap-1">
            {versionLinkSlot}
          </div>
        ) : null}
        {children ? (
          <span className="text-caption font-caption text-default-font">
            {children}
          </span>
        ) : null}
        {linkSlot ? (
          <div className="flex w-full flex-col items-start gap-2">
            {linkSlot}
          </div>
        ) : null}
      </div>
    );
  }
);

interface Dc_Popover_Card_CiteAsRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  version?: React.ReactNode;
  children?: React.ReactNode;
  doi?: React.ReactNode;
  copySlot?: React.ReactNode;
  linkSlot?: React.ReactNode;
  versionLinkSlot?: React.ReactNode;
  className?: string;
}

const Dc_Popover_Card_CiteAsRoot = React.forwardRef<
  HTMLDivElement,
  Dc_Popover_Card_CiteAsRootProps
>(function Dc_Popover_Card_CiteAsRoot(
  {
    icon = <FeatherLayers2 />,
    version,
    children,
    doi,
    copySlot,
    linkSlot,
    versionLinkSlot,
    className,
    ...otherProps
  }: Dc_Popover_Card_CiteAsRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex max-w-[448px] flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-3 shadow-lg comp-popover",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <CiteAsContent
        version={version}
        linkSlot={
          linkSlot ? (
            <div className="flex flex-col items-start">{linkSlot}</div>
          ) : null
        }
        versionLinkSlot={
          versionLinkSlot ? (
            <div className="flex items-start gap-1">{versionLinkSlot}</div>
          ) : null
        }
      >
        {children}
      </CiteAsContent>
      {copySlot ? (
        <div className="flex w-full items-center gap-2">{copySlot}</div>
      ) : null}
    </div>
  );
});

export const Dc_Popover_Card_CiteAs = Object.assign(
  Dc_Popover_Card_CiteAsRoot,
  {
    CopyInteraction,
    CiteAsContent,
  }
);
